import {
  type ActivityLog,
  type Diagnosis,
  type Event,
  type Property,
  type ProposalIntakeForm,
  type QuoteDocument,
  type SCLinkResponse,
  type ServicesRendered,
  type User,
} from '@kalos/kalos-rpc';
import { createContext, type RefObject, useContext } from 'react';

import { type GeolocationPermission } from '../../../tools/geolocation';
import type { LegacyServiceRow } from '../../utils/legacyServiceRows';

// Overall job progress stages (includes both time tracking and job workflow)
export type JobStep =
  | 'scheduled'
  | 'enroute'
  | 'oncall'
  | 'diagnose'
  | 'proposal'
  | 'invoice'
  | 'complete';

// Job workflow actions (activity log names - job workflow only, not time tracking)
// Time tracking uses: Enroute, On Call, Complete, Incomplete
export type WorkflowAction =
  | 'Diagnose'
  | 'Create Proposal'
  | 'Create Invoice'
  | 'Job Workflow Complete';

// Employee status derived from activity logs
export type EmployeeStatus = 'enroute' | 'on-call' | 'idle';

// Time tracking status
export type TimeTrackingStatus = 'not-started' | 'enroute' | 'on-call' | 'complete' | 'incomplete';

// A single timesheet-visible time tracking event for the timeline
export interface TimeTrackingEvent {
  status: Exclude<TimeTrackingStatus, 'not-started'>;
  name: string;
  timestamp: string;
  /** The services_rendered row behind this event; pairs an On Call row with its ServiceChannel arrival outcome. */
  servicesRenderedId: number;
  technicianUserId: number;
  /**
   * How the visit ended, when a Finish visit close-out said more than the row's Completed or
   * Incomplete (Part on order, Pending Approval). Shown in place of the status label.
   */
  outcomeLabel?: string;
}

/**
 * Where a technician's On Call check-in stands at ServiceChannel (86bc18e7q).
 *
 * `sending` is the request in flight. The rest mirror SCVisitOutcome.state:
 * `recorded` is on ServiceChannel's record; `not_recorded` was refused (see
 * refusalKind) and the call is not billable until it is recorded; `pending`
 * has not gone through yet and the connector keeps retrying with the original
 * arrival time; `failed` stopped retrying; `skipped` means the subscriber is
 * switched off; `superseded` means the technician checked out of the visit
 * before the arrival went, so it is withheld rather than sent after the
 * departure (F7).
 * Nothing here ever gates the technician's own status or timer.
 */
export type ArrivalState =
  | 'sending'
  | 'recorded'
  | 'not_recorded'
  | 'pending'
  | 'failed'
  | 'skipped'
  | 'superseded';

export interface ArrivalStatus {
  state: ArrivalState;
  servicesRenderedId: number;
  /** One of the connector's refusal classes when state is not_recorded. */
  refusalKind: string;
  scErrorCode: number;
  /** Miles from the offered coordinate to the Kalos property; 0 when unknown. */
  distanceMiles: number;
  maxRadiusMiles: number;
  occurredAt: string;
  detail: string;
  /** Whether sending again, with nothing changed outside Kalos, can succeed (86bc12cgh). */
  retryable?: boolean;
  /** ServiceChannel's own ErrorMessage from the refusal, when it gave one. */
  scErrorMessage?: string;
  /** The status push reached ServiceChannel: for a check-out, the close-out was accepted. */
  statusDelivered?: boolean;
  /** The ledger row behind this send, as the dispatch card's Resend link names it (`?scResend=`). */
  syncEventId?: string;
}

/** The connector's refusal class for a check-in that carried no coordinate. */
export const ARRIVAL_REFUSAL_NO_LOCATION = 'location denied or not captured';
/** The connector's refusal class for a coordinate outside the geofence. */
export const ARRIVAL_REFUSAL_OUT_OF_RANGE = 'out of range';
/** The connector's refusal class for ServiceChannel refusing check-in to Kalos's account (804, 403). */
export const ARRIVAL_REFUSAL_PERMISSION = 'permission refused';
/** The connector's refusal class for ServiceChannel not having granted the app check-in (504, 401). */
export const ARRIVAL_REFUSAL_NOT_ENTITLED = 'ServiceChannel has not granted check-in';

// Time tracking actions
// 'Arrive On Call' is the En Route → On Call step; 'Go On Call' skips the
// route leg entirely (tech is already on site), writing no Enroute segment.
// 'Finish visit' is the one close-out action offered at On Call.
export type TimeTrackingAction =
  | 'Start Route'
  | 'Arrive On Call'
  | 'Go On Call'
  | 'Finish visit'
  | 'Return En Route';

/** How a visit ended, as the Finish visit close-out asks it (86bc12cgh). */
export type FinishVisitOutcome =
  | 'completed'
  | 'part-on-order'
  | 'part-received'
  | 'quote-requested'
  | 'pending-approval'
  | 'incomplete';

/** What the Finish visit close-out submits: exactly what the form showed. */
export type FinishVisitPayload = {
  outcome: FinishVisitOutcome;
  /** Required; also written to Notes for Customer so the two hold the same text. */
  servicesRendered: string;
  /** Always answered; the form starts on No. */
  refrigerantUsed: boolean;
  /**
   * Who signs a Yes: the certified colleague the technician named, or 0 for the technician's
   * own card (86bc12cgh, co-04 / co-06). The server refuses a Yes whose signer has no card.
   */
  refrigerantSignedBy: number;
};

/**
 * What a close-out wrote: `saved`, all of it; `partial`, the close-out row (so time is closed and
 * the ServiceChannel check-out is queued) but not the job status or its history.
 */
export type MarkJobFinishResult = 'saved' | 'partial';

// Current workflow state for the technician
export interface TechnicianWorkflowState {
  lastAction: WorkflowAction | null; // Last activity log action for this tech
  nextAction: WorkflowAction | null; // Next available action
  employeeStatus: EmployeeStatus; // Derived from current workflow state
}

/** Unsaved job-information form edits, stashed on unmount so tab switches don't lose them */
export type JobInfoDraft = {
  eventId: number;
  values: Record<string, unknown>;
  dateStarted?: Date;
  dateEnded?: Date;
  datesDirty: boolean;
};

export interface JobDetailsContextType {
  // Data
  event: Event | null;
  customer: User | null;
  property: Property | null;
  quoteDocument: QuoteDocument | null;
  /** Legacy service rows (servicesperformedrow1-4) for billing and Services Rendered */
  legacyServiceRows: LegacyServiceRow[];
  /** All ServicesRendered records for this event — shared to avoid duplicate fetches */
  servicesRendered: ServicesRendered[];
  isServicesRenderedLoading: boolean;
  activityLogs: ActivityLog[]; // Activity log entries for event feed
  diagnoses: Diagnosis[]; // Diagnoses for this event
  proposalIntakeForm: ProposalIntakeForm | null; // Repair/AOR proposal intake form for this event
  originIntakeForm: ProposalIntakeForm | null; // Intake form from the originating job when this is a follow-up
  hasDraftIntakeForm: boolean; // Whether there's a draft repair/AOR intake form
  saleSheetForm: ProposalIntakeForm | null; // Sale sheet intake form for this event
  hasDraftSaleSheetForm: boolean; // Whether there's a draft sale sheet intake form
  movedFollowUpDoc: QuoteDocument | null; // Follow-up doc that was moved to a different job
  getJobInfoDraft: () => JobInfoDraft | null; // Unsaved job-information edits held across tab switches
  setJobInfoDraft: (draft: JobInfoDraft | null) => void;

  // Loading states
  isEventLoading: boolean;
  isCustomerLoading: boolean;
  isPropertyLoading: boolean;
  isWorkflowLoading: boolean; // Loading memos to determine workflow state
  isQuoteDocumentLoading: boolean;
  isGlobalLoading: boolean; // Global loading state for actions (time tracking, workflow, etc.)
  /** Shown under the spinner when `isGlobalLoading` is true; set via `setGlobalLoading`. */
  globalLoadingMessage: string | undefined;
  isDiagnosesLoading: boolean;

  // Error states
  eventError: Error | null;
  customerError: Error | null;
  propertyError: Error | null;

  // Job Progress (overall stages)
  currentStep: JobStep;
  progress: number;
  setCurrentStep: (step: JobStep) => void;

  // Technician Workflow State (derived from activity logs)
  workflowState: TechnicianWorkflowState;

  // Time Tracking State
  timeTrackingStatus: TimeTrackingStatus;
  /** The logged-in tech's newest time tracking row, the one the status reads; 0 when none. */
  timeTrackingServicesRenderedId: number;
  timeTrackingElapsedMinutes: number;
  /** Timesheet-visible time tracking events for the logged-in tech, chronological */
  timeTrackingEvents: TimeTrackingEvent[];
  /** Total minutes on the job across closed time tracking records */
  timeOnJobMinutes: number;
  availableTimeTrackingActions: TimeTrackingAction[];
  handleTimeTrackingAction: (action: TimeTrackingAction) => Promise<void>;

  // ServiceChannel check-in feedback (86bc18e7q)
  /**
   * The job's ServiceChannel link as GetJobLink last read it, re-read after a check-in and while
   * an arrival is pending. One read for the page, so the phone card and the arrival agree.
   */
  serviceChannelLink: SCLinkResponse | undefined;
  /** The job has a ServiceChannel work order link; On Call sends a check-in. */
  isServiceChannelJob: boolean;
  /** Linked, but the customer's ServiceChannel subscriber is switched off: nothing is sent. */
  isServiceChannelSyncPaused: boolean;
  /** The latest On Call check-in's outcome, or null when none has been sent. */
  arrival: ArrivalStatus | null;
  /**
   * The arrival's visit is already checked out (a Completed or Incomplete row follows its On
   * Call row), so it is not reopened and nothing is offered to resend it (F7).
   */
  isArrivalCheckedOut: boolean;
  isArrivalDialogOpen: boolean;
  openArrivalDialog: () => void;
  closeArrivalDialog: () => void;
  /** Re-captures the position onto the On Call row and sends the check-in again. */
  retryArrival: () => Promise<void>;
  /** Device location permission, as far as the browser will say. */
  locationPermission: GeolocationPermission;
  refreshLocationPermission: () => Promise<GeolocationPermission>;
  /** Triggers the browser's location prompt and reports the permission afterwards. */
  requestLocation: () => Promise<GeolocationPermission>;

  // ServiceChannel check-out feedback (86bc12cgh, slice 3)
  /** The latest close-out's outcome at ServiceChannel, or null when none has been sent. */
  checkout: ArrivalStatus | null;
  /** How that visit ended, as the job status spells it ("Part on order"), for the dialog's lead. */
  checkoutOutcomeLabel: string | null;
  /**
   * A Completed close-out ServiceChannel has not yet accepted: the job waits outside Admin
   * Review until it does (completion_hold.go), and the page says so.
   */
  isCompletionHeld: boolean;
  isCheckoutDialogOpen: boolean;
  openCheckoutDialog: () => void;
  closeCheckoutDialog: () => void;
  /** Re-captures the position onto the close-out row and sends the check-out again. */
  retryCheckout: () => Promise<void>;

  // Finish visit close-out (86bc12cgh).
  isFinishVisitOpen: boolean;
  openFinishVisit: () => void;
  closeFinishVisit: () => void;
  /**
   * Sets the job status, then writes the close-out row, then the history. Throws when the job
   * status or the close-out row did not save (nothing after it is written then, and the dialog
   * stays open to try again); resolves `partial` when only the history did not.
   */
  submitFinishVisit: (payload: FinishVisitPayload) => Promise<MarkJobFinishResult>;

  // Workflow action handler (creates activity log for system-generated events)
  // completionStatus is optional and only used for 'Complete' action
  handleWorkflowAction: (
    action: WorkflowAction,
    completionStatus?: 'Completed' | 'Incomplete',
  ) => Promise<void>;

  // Navigation
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  /** Top of the Systems tab panel (job details) — for scroll-into-view after diagnosis save */
  systemsTabTopRef: RefObject<HTMLDivElement | null>;
  scrollJobSystemsTabToTop: () => void;
  /** Overview-tab Job Notes feed section — "All notes" link scroll target */
  jobNotesSectionRef: RefObject<HTMLDivElement | null>;
  scrollToJobNotesSection: () => void;
  /** Scrolls to the Activity Feed and forces its filter to Notes */
  viewAllNotes: () => void;
  notesFilterRequestId: number;
  /** The Overview card's Complete Diagnosis dialog, here so the phone's bottom bar can open it. */
  isCompleteDiagnosisDialogOpen: boolean;
  setIsCompleteDiagnosisDialogOpen: (open: boolean) => void;
  /** Switches to Overview and opens the Complete Diagnosis dialog on its card. */
  openCompleteDiagnosis: () => void;
  /**
   * Phones: a request from outside Job Information (the header's Service needed) to open one of
   * its edit sheets. `requestId` changes on every request so the same group can be asked twice.
   */
  jobInfoEditRequest: { group: string; requestId: number } | null;
  requestJobInfoEdit: (group: string) => void;
  /** Called once Job Information has opened the requested sheet. */
  clearJobInfoEditRequest: () => void;

  // Dialog state and controls
  isProposalDialogOpen: boolean;
  isInvoiceDialogOpen: boolean;
  isCompleteDialogOpen: boolean;
  openProposalDialog: () => void;
  closeProposalDialog: () => void;
  openInvoiceDialog: () => void;
  closeInvoiceDialog: () => void;
  openCompleteDialog: () => void;
  closeCompleteDialog: () => void;

  // Update handlers
  updateEvent: (updates: Partial<Event>) => Promise<void>;
  refreshEvent: () => Promise<void>;
  refreshCustomer: () => void;
  refreshProperty: () => void;
  refreshAll: () => void;
  setGlobalLoading: (loading: boolean, message?: string) => void;

  refreshVersion: number;

  // Feature flags - when true, hides specific flows
  hideFollowUpJobFlow: boolean;
  hideIntakeFormFlow: boolean;

  /** Job status is Archived — most edits are blocked (see isArchiveAdmin for exceptions). */
  isJobArchived: boolean;
  /** User may edit job information on an archived job (ArchiveAdmin permission). */
  isArchiveAdmin: boolean;
}

export const JobDetailsContext = createContext<JobDetailsContextType | undefined>(undefined);

export const useJobDetails = () => {
  const context = useContext(JobDetailsContext);
  if (!context) {
    throw new Error('useJobDetails must be used within a JobDetailsProvider');
  }
  return context;
};
